import { useEffect, useRef, useState } from 'react';
import { useUser } from '../context/UserContext';
import { useProjectOptional } from '../context/ProjectContext';
import ForceRefreshModal from '../components/elements/ForceRefreshModel';

const RoleChangeGuard = () => {
  const { roleChanged, clearRoleChanged } = useUser();
  const projectCtx = useProjectOptional();

  const [projectRoleChanged, setProjectRoleChanged] = useState(false);
  const prevProjectRoleRef = useRef(null);
  const prevProjectIdRef = useRef(null);

  useEffect(() => {
    if (!projectCtx?.isReady) return;

    const currentProjectId = projectCtx.projectId ?? null;
    const currentRole = projectCtx.projectRole ?? null;

    // Reset tracking when project changes or user leaves a project
    if (currentProjectId !== prevProjectIdRef.current) {
      prevProjectIdRef.current = currentProjectId;
      prevProjectRoleRef.current = currentRole;
      return;
    }

    // Only flag a change when staying in the same project and both values are non-null
    if (
      prevProjectRoleRef.current !== null &&
      currentRole !== null &&
      prevProjectRoleRef.current !== currentRole
    ) {
      setProjectRoleChanged(true);
    }

    prevProjectRoleRef.current = currentRole;
  }, [projectCtx?.projectRole, projectCtx?.projectId, projectCtx?.isReady]);

  const isProjectRoute = Boolean(projectCtx?.projectId);
  const orgRoleChangeAffectsCurrentProject = roleChanged && isProjectRoute;
  const isOpen = roleChanged || projectRoleChanged;
  const message = projectRoleChanged || orgRoleChangeAffectsCurrentProject
    ? 'Your role has been updated and your project permissions may have changed. Please refresh the page to apply the new permissions.'
    : 'Your organization role has been updated. Please refresh to continue with your new permissions.';

  const handleConfirm = () => {
    clearRoleChanged();
    setProjectRoleChanged(false);
    window.location.reload();
  };

  return (
    <ForceRefreshModal
      open={isOpen}
      title="Your Role Has Changed"
      message={message}
      onConfirm={handleConfirm}
      hideCancel={true}
    />
  );
};

export default RoleChangeGuard;